import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang, CONTACT } from "../i18n";

const waHref = (label) => `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(label)}`;

export default function Header() {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "historia", label: t.nav.historia },
    { id: "servicios", label: t.nav.servicios },
    { id: "galeria", label: t.nav.galeria },
    { id: "opiniones", label: t.nav.opiniones },
    { id: "contacto", label: t.nav.contacto },
  ];

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8 h-20 md:h-24 flex items-center justify-between">
        <button onClick={() => go("top")} data-testid="logo-btn" className="flex items-center gap-2 group">
          <img
            src="https://customer-assets-rejwkqb3.emergentagent.net/job_reformas-acero/artifacts/x4bhwdrh_WhatsApp%20Image%202026-08-09%20at%2022.00.56.jpeg"
            alt="Acero Construcciones y Reformas"
            className="h-14 md:h-16 w-auto object-contain"
          />
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className="text-sm font-medium text-steel-300 hover:text-white transition-colors duration-300 relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-brand-orange hover:after:w-full after:transition-[width] after:duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="text-xs font-bold tracking-widest border border-white/20 rounded-full px-3 py-1.5 hover:border-brand-orange hover:text-brand-orange transition-colors duration-300"
          >
            {lang === "es" ? "EN" : "ES"}
          </button>
          <a
            href={waHref(t.hero.cta)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-cta"
            className="hidden sm:inline-flex items-center bg-brand-orange text-black font-semibold text-sm px-5 py-2.5 rounded-full hover:bg-white transition-colors duration-300"
          >
            {t.nav.cta}
          </a>
          <button className="lg:hidden p-2 -mr-2" onClick={() => setOpen(true)} data-testid="menu-open">
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex justify-end p-5">
              <button onClick={() => setOpen(false)} data-testid="menu-close" className="p-2">
                <X className="h-7 w-7" />
              </button>
            </div>
            <div className="flex flex-col items-start gap-6 px-8 mt-8">
              {links.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => go(l.id)}
                  className="font-display text-4xl font-semibold tracking-tight"
                >
                  {l.label}
                </motion.button>
              ))}
              <a
                href={waHref(t.hero.cta)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 bg-brand-orange text-black font-semibold px-6 py-3 rounded-full"
              >
                {t.nav.cta}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
